import React from 'react';
import { Redirect, Stack, usePathname } from 'expo-router';
import type { Href } from 'expo-router';
import { View, ActivityIndicator } from 'react-native';
import { useAuth } from '../../src/context/AuthContext';
import { pathForPhase, isPublicPhase } from '../../src/navigation/phaseRedirect';
import {
  clearCheckoutReturnMentorProfile,
  peekCheckoutReturnMentorProfile,
} from '../../src/utils/checkoutCompletion';
import { resolvePostCheckoutRoute } from '../../src/utils/postCheckoutRoute';

// Token-based public routes that must always render regardless of auth state.
// These represent fresh actions tied to a one-time token (invitation acceptance,
// magic-link sign-in) — they should never redirect an authed
// user away because the user may be on a shared device, may want to accept an
// invite as a different account, or may be following a link from email while
// already logged in. Tokens fail safely on the backend if invalid/expired.
const TOKEN_BASED_PUBLIC_ROUTES = [
  '/accept-invitation',
  '/auth/magic-link',
  // Needs the signed-in session that requested the change (SVC-1).
  '/confirm-email-change',
];

function PublicStack() {
  return (
    <Stack screenOptions={{ headerShown: false }}>
      {/* Existing public screens */}
      <Stack.Screen name="login" />
      <Stack.Screen name="register" />
      <Stack.Screen name="signup" />
    </Stack>
  );
}

function AuthGate() {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
      <ActivityIndicator size="large" color="#2563eb" />
    </View>
  );
}

export default function PublicLayout() {
  const pathname = usePathname();
  const { phase, user, billingStatus } = useAuth();

  // A Stripe return landing (`/?status=success`) is routed once with the
  // mentor check below; later redirects route by phase only.
  React.useEffect(() => {
    if (phase !== 'loading' && !isPublicPhase(phase)) {
      clearCheckoutReturnMentorProfile();
    }
  }, [phase]);

  const isTokenBasedRoute = TOKEN_BASED_PUBLIC_ROUTES.some((route) =>
    pathname === route || pathname.startsWith(`${route}?`) || pathname.startsWith(`${route}/`)
  );

  // Token-based routes (accept-invitation, magic-link) must always render —
  // they take action via the URL token, not the current session — and must
  // render even while auth is still resolving,
  // so a magic-link consume isn't blocked on the /me round trip.
  if (isTokenBasedRoute) {
    return <PublicStack />;
  }

  // Guard BEFORE rendering the login/signup screens. Previously this ran in a
  // post-render useEffect, so an already-authenticated user hard-loading /login
  // saw the login form flash before being redirected. Rendering the gate while
  // phase==='loading' and a <Redirect> once resolved eliminates that flash.
  if (phase === 'loading') {
    return <AuthGate />;
  }

  if (!isPublicPhase(phase)) {
    const checkoutReturnMentorProfile = peekCheckoutReturnMentorProfile();
    const target = checkoutReturnMentorProfile === null
      ? pathForPhase(phase)
      : resolvePostCheckoutRoute({
        phase,
        user,
        billingStatus,
        mentorProfile: checkoutReturnMentorProfile,
      });
    return <Redirect href={target as Href} />;
  }

  return <PublicStack />;
}
